// src/components/results/peerText.js
//
// Comparisons with other Logami students, positive only. The numbers come from the
// nightly resultNorms function (each student once, best real attempt, admin and
// abandoned attempts left out). The thresholds below were agreed with the teacher:
// a line only appears when it is both encouraging and backed by enough students.
import { useEffect, useState } from 'react';

export const RANK_MIN_STUDENTS = 30;    // overall ranking
export const RANK_MIN_PCT = 50;         // only "top half"
export const QUESTION_MIN_STUDENTS = 30;
export const FAMILY_MIN_STUDENTS = 20;
export const FAMILY_MIN_MISS = 0.4;     // "catches students X% of the time" only when common

const cache = new Map();

/** Dev harness only: put numbers in the cache so the page shows them without the function. */
export function primeResultNorms(provider, level, norms) {
  cache.set(`${String(provider).toLowerCase()}-${String(level).toLowerCase()}`, norms);
}

/** The peer statistics for one exam (null while loading, or when there are none). */
export function useResultNorms(provider, level, enabled = true) {
  const key = `${String(provider || '').toLowerCase()}-${String(level || '').toLowerCase()}`;
  const [norms, setNorms] = useState(() => cache.get(key) || null);
  useEffect(() => {
    if (!enabled || !provider || !level) return undefined;
    if (cache.has(key)) { setNorms(cache.get(key)); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase');
        const { data } = await httpsCallable(functions, 'getResultNorms')({ provider, level });
        cache.set(key, data?.norms || null);
        if (!cancelled) setNorms(data?.norms || null);
      } catch (err) {
        // No numbers is fine: every line below simply stays away.
        console.warn('Peer statistics not loaded:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [key, provider, level, enabled]);
  return norms;
}

/** "Better than X% of the N other students" → { pct, students } or null. */
export function rankOf(norms, ratio) {
  const scores = norms?.scores || [];
  if (scores.length < RANK_MIN_STUDENTS || !Number.isFinite(ratio)) return null;
  // The student's own best attempt is one of the scores: compare with the others.
  const others = scores.length - 1;
  const pct = Math.min(99, Math.floor((scores.filter((r) => r < ratio).length / others) * 100));
  return pct >= RANK_MIN_PCT ? { pct, students: others } : null;
}

/** One question: a hard one you got right, or a common miss. → { key, pct } or null */
export function questionPeer(norms, file, item) {
  // keyed partId/questionId (older norms: questionId only)
  const rec = norms?.questions?.[file]?.[`${item.partId}/${item.questionId}`] ?? norms?.questions?.[file]?.[item.questionId];
  if (!rec || rec[0] < QUESTION_MIN_STUDENTS) return null;
  const right = rec[1] / rec[0];
  if (item.correct && right <= 0.5) return { key: 'examResults.story.peer.hardRight', pct: Math.round(right * 100) };
  if (!item.correct && 1 - right >= 0.5) return { key: 'examResults.story.peer.commonMiss', pct: Math.round((1 - right) * 100) };
  return null;
}

/** A trap or language family that catches many students. key: "trap:opposite" etc. → pct or null */
export function familyMissPct(norms, key) {
  const f = norms?.families?.[key];
  if (!f || f.students < FAMILY_MIN_STUDENTS || !f.items) return null;
  const miss = f.wrong / f.items;
  return miss >= FAMILY_MIN_MISS ? Math.round(miss * 100) : null;
}
